import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { RefreshCw, AlertTriangle, CheckCircle, Clock, ExternalLink } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface WebhookStatus {
  status: 'healthy' | 'warning' | 'error' | 'unknown'
  lastReceived: string | null
  recentCount: number
  latestEvents: any[]
  errorCount: number
}

export function AutoRepliesWebhookMonitor() {
  const [status, setStatus] = useState<WebhookStatus>({
    status: 'unknown',
    lastReceived: null,
    recentCount: 0,
    latestEvents: [],
    errorCount: 0
  })
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const testMissedReply = async () => {
    try {
      setLoading(true)
      console.log('🧪 Testing missed reply resend...')
      
      // Get a recent EMAIL_REPLY from obsync_dynamic_reports that was missed
      const { data: missedReply, error: fetchError } = await supabase
        .from('obsync_dynamic_reports')
        .select('*')
        .eq('event_type', 'EMAIL_REPLY')
        .gt('created_at', '2025-09-04T15:16:48')
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (fetchError || !missedReply) {
        toast({
          title: "No Missed Replies",
          description: "No recent EMAIL_REPLY events found to resend",
          variant: "destructive"
        })
        return
      }

      console.log('📧 Found missed reply:', missedReply)

      // Transform the obsync data to match what auto-replies-tagger expects
      const replyData = missedReply.data as any
      const transformedData = {
        ...replyData,
        // Ensure required fields are present
        reply_body: replyData.reply_message?.text || replyData.reply_message?.html || '',
        reply_message: replyData.reply_message?.text || replyData.reply_message?.html || '',
      }

      // Call the auto-replies-tagger function with the transformed data
      const { data, error } = await supabase.functions.invoke('auto-replies-tagger', {
        body: transformedData
      })

      if (error) {
        console.error('❌ Test failed:', error)
        toast({
          title: "Test Failed",
          description: `Error: ${error.message}`,
          variant: "destructive"
        })
      } else {
        console.log('✅ Test successful:', data)
        const replyData = missedReply.data as any
        toast({
          title: "Missed Reply Resent Successfully",
          description: `Resent reply from ${replyData?.from_email || 'unknown'} to auto_replies_tagger`,
        })
        // Refresh the monitor
        setTimeout(checkWebhookHealth, 2000)
      }
    } catch (error) {
      console.error('❌ Test error:', error)
      toast({
        title: "Test Error",
        description: "Failed to resend missed reply",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const checkWebhookHealth = async () => {
    try {
      setLoading(true)
      
      // Check recent obsync_dynamic_reports entries (last 24 hours)
      const { data: recentReplies, error: repliesError } = await supabase
        .from('obsync_dynamic_reports')
        .select('*')
        .eq('report_type', 'EMAIL_REPLY')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(10)

      if (repliesError) {
        console.error('Error fetching recent replies:', repliesError)
        setStatus(prev => ({ ...prev, status: 'error', errorCount: prev.errorCount + 1 }))
        return
      }

      // Check obsync_dynamic_reports for comparison (EMAIL_REPLY events in last 24 hours)
      const { data: obsyncReplies, error: obsyncError } = await supabase
        .from('obsync_dynamic_reports')
        .select('created_at, event_type, from_email, to_email, campaign_name')
        .eq('event_type', 'EMAIL_REPLY')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(10)

      if (obsyncError) {
        console.error('Error fetching obsync replies:', obsyncError)
      }

      const autoRepliesCount = recentReplies?.length || 0
      const obsyncRepliesCount = obsyncReplies?.length || 0
      const lastReceived = recentReplies?.[0]?.created_at || null
      const obsyncLastReceived = obsyncReplies?.[0]?.created_at || null

      // Determine health status
      let healthStatus: 'healthy' | 'warning' | 'error' = 'healthy'
      
      // If obsync has recent replies but auto_replies_tagger doesn't, there's an issue
      if (obsyncRepliesCount > 0 && autoRepliesCount === 0) {
        healthStatus = 'error'
      } else if (obsyncRepliesCount > autoRepliesCount * 1.5) {
        // If obsync has significantly more replies, there might be a sync issue
        healthStatus = 'warning'
      } else if (autoRepliesCount === 0 && obsyncRepliesCount === 0) {
        // No recent activity in either table
        healthStatus = 'warning'
      }

      // Check if last received is too old (more than 6 hours behind obsync)
      if (obsyncLastReceived && lastReceived) {
        const timeDiff = new Date(obsyncLastReceived).getTime() - new Date(lastReceived).getTime()
        if (timeDiff > 6 * 60 * 60 * 1000) { // 6 hours
          healthStatus = 'error'
        }
      }

      setStatus({
        status: healthStatus,
        lastReceived,
        recentCount: autoRepliesCount,
        latestEvents: recentReplies || [],
        errorCount: 0
      })

    } catch (error) {
      console.error('Error checking webhook health:', error)
      setStatus(prev => ({ 
        ...prev, 
        status: 'error', 
        errorCount: prev.errorCount + 1 
      }))
      toast({
        title: "Monitor Error",
        description: "Failed to check webhook health",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    checkWebhookHealth()
    
    // Set up real-time monitoring
    const channel = supabase
      .channel('auto-replies-monitor')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'auto_replies_tagger'
        },
        () => {
          console.log('New auto reply received, refreshing monitor...')
          checkWebhookHealth()
        }
      )
      .subscribe()

    // Refresh every 5 minutes
    const interval = setInterval(checkWebhookHealth, 5 * 60 * 1000)

    return () => {
      supabase.removeChannel(channel)
      clearInterval(interval)
    }
  }, [])

  const getStatusIcon = () => {
    switch (status.status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-red-500" />
      default:
        return <Clock className="h-5 w-5 text-gray-500" />
    }
  }

  const getStatusColor = () => {
    switch (status.status) {
      case 'healthy':
        return 'bg-green-500'
      case 'warning':
        return 'bg-yellow-500'
      case 'error':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getStatusText = () => {
    switch (status.status) {
      case 'healthy':
        return 'Healthy'
      case 'warning':
        return 'Warning'
      case 'error':
        return 'Error'
      default:
        return 'Unknown'
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {getStatusIcon()}
              Auto-Replies Webhook Monitor
            </CardTitle>
            <CardDescription>
              Monitoring auto_replies_tagger table for incoming webhooks
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`${getStatusColor()} text-white border-0`}>
              {getStatusText()}
            </Badge>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={checkWebhookHealth}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={testMissedReply}
              disabled={loading}
            >
              Test Missed Reply
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">Last Received</p>
            <p className="text-sm text-muted-foreground">
              {status.lastReceived ? formatTimeAgo(status.lastReceived) : 'Never'}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Recent Count (24h)</p>
            <p className="text-sm text-muted-foreground">
              {status.recentCount} replies
            </p>
          </div>
        </div>

        {status.latestEvents.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Latest Events</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {status.latestEvents.slice(0, 5).map((event, index) => (
                <div 
                  key={event.id || index} 
                  className="flex items-center justify-between p-2 bg-muted rounded text-sm"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {event.from_email || 'Unknown sender'}
                    </p>
                    <p className="text-muted-foreground truncate">
                      {event.campaign_name || 'Unknown campaign'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-muted-foreground">
                      {formatTimeAgo(event.created_at)}
                    </p>
                    {event.app_url && (
                      <a 
                        href={event.app_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        View <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {status.status === 'error' && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">
              <strong>Issue Detected:</strong> Auto-replies webhook may not be working properly. 
              Check edge function logs for errors.
            </p>
          </div>
        )}

        {status.status === 'warning' && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> Webhook sync may be delayed or there's low activity.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}